<template>
  <v-btn
    flat
    :prepend-icon="icon"
    :append-icon="iconRight"
    :disabled="disabled"
    class="text-capitalize"
    :class="classObject"
    :variant="variant"
    :loading="props.loading"
    :type="props.btnType"
  >
    <div v-if="$slots['icon']">
      <slot name="icon" />
    </div>
    {{ label }}
    <slot />
    <div v-if="$slots['iconRight']">
      <slot name="iconRight" />
    </div>
  </v-btn>
</template>

<script setup>
import { computed, defineProps } from 'vue'

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    default: '',
  },
  iconRight: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'large',
    validator(value) {
      return ['large', 'medium', 'small'].includes(value)
    },
  },
  type: {
    type: String,
    default: 'primary',
    validator(value) {
      return ['delete', 'primary', 'secondary', 'text', 'plain'].includes(value)
    },
  },
  loading: {
    type: Boolean,
    default: false,
  },
  // binding to HTMLButton 'type' property because we use the type prop for button style
  btnType: {
    type: String,
    default: 'button',
  },
})

const classObject = computed(() => ({
  'large-button': props.size === 'large',
  'small-button': props.size === 'small',
  'medium-button': props.size === 'medium',
  primary: props.type === 'primary',
  secondary: props.type === 'secondary',
  delete: props.type === 'delete',
  'body-desktop': props.type === 'text',
  'body-semi-bold': ['large', 'medium'].includes(props.size) && props.type !== 'text',
  'caption1-desktop': props.size === 'small' && props.type !== 'text',
  'icon-left': props.icon,
  'icon-right': props.iconRight,
}))

const variant = computed(() => {
  if (props.type === 'delete') {
    return 'outlined'
  }
  else if (props.type === 'text') {
    return 'text'
  }
  else if (props.type === 'plain') {
    return 'plain'
  }
  else {
    return 'flat'
  }
})
</script>

<style lang="scss" scoped>
.icon-left {
  margin-left: 0 !important;
  margin-right: 4px !important;
}
.icon-right {
  margin-right: 0 !important;
  margin-left: 4px !important;
}

button.v-btn, a.v-btn {
  border-radius: 3px;
  letter-spacing: initial;

  &.large-button {
    padding: 6px 8px 6px 6px;
    height: 40px;
    min-width: initial !important;

    :deep(.v-icon) {
      font-size: 24px;
    }

    &.icon-left {
      :deep(.v-btn__prepend) {
        margin-left: 0 !important;
        margin-right: 0 !important;
      }
    }
    &.icon-right {
      &:deep(.v-btn__append) {
        margin-right: 0 !important;
        margin-left: 2px !important;
      }
    }
  }

  &.medium-button {
    height: 2.25rem;
    padding: 0.5rem;
    min-width: initial !important;
  }

  &.small-button {
    padding: 4px 8px;
    height: auto;
    min-width: initial !important;

    :deep(.v-icon) {
      margin-right: 6px;
      font-size: 20px;
    }

    &.icon-left {
      :deep(.v-btn__prepend) {
        margin-left: 0 !important;
        margin-right: 0 !important;
      }
    }
    &.icon-right {
      padding-right: 2px !important;

      &:deep(.v-btn__append) {
        margin-left: 6px !important;
        margin-right: 0 !important;
      }
    }
  }

  &.primary {
    background-color: rgb(var(--v-theme-primary));
    border: 1px solid rgb(var(--v-theme-primary));
    color: #FFFFFF;

    &.v-btn--disabled {
      border: none;
      cursor: not-allowed !important;
      opacity: 1!important;
    }

    &.success {
      background-color: rgb(var(--v-theme-success));
      border-color: rgb(var(--v-theme-success));
    }
  }

  &.secondary {
    &:not(.v-btn--disabled) {
      border: 1px solid currentColor;
      color: rgb(var(--v-theme-primary));
    }

    &.v-btn--disabled {
      cursor: not-allowed !important;
      opacity: 1!important;
    }
    &.success {
      color: rgb(var(--v-theme-success));
    }
    &:hover {
      // background: var(--alice-blue) !important;
    }
  }

  &.delete {
      color: var(--nintendo-red);
      background: #FFFFFF !important;

      &::before {
        border-color: var(--nintendo-red);
      }

      &:hover {
        color: #FFFFFF;
        background: var(--nintendo-red) !important;
        &.v-btn--variant-outlined {
          border-color: var(--nintendo-red);

        }
      }
    }

  &.icon-right {
    padding-right: 6px !important;
    :deep(.v-btn__append) {
      margin-right: 0 !important;
      margin-left: 2px !important;
    }
  }
}
a.v-btn.v-btn--active:not(:hover) {
  :deep(> .v-btn__overlay) {
    opacity: 0;
  }
}

// button.q-btn {
//   &.big-button {

//     &.delete {
//       color: var(--nintendo-red) !important;
//       background: #FFFFFF !important;

//       &::before {
//         border-color: var(--nintendo-red);
//       }

//       &:hover {
//         color: #FFFFFF !important;
//         background: var(--nintendo-red) !important;
//       }
//     }
//   }
// }

</style>